import React, { useState, useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useFetchData } from 'services/queries';
import { PAYMENT_ENDPOINTS, DEFAULT_UNSELECTED_ORDER_STATUSES } from '../constants';
import PaymentCurrencyLanes from './components/PaymentCurrencyLanes';
import OverdueAgingSection from './components/OverdueAgingSection';
import ProvisionForecastSection from './components/ProvisionForecastSection';
import CashFlowChart from './components/CashFlowChart';

const PaymentDashboard = () => {
  const { data: statuses } = useFetchData('order/status-sales-order/');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Once statuses load, auto-select all except the default unselected ones.
  // New statuses added later in the system are automatically included.
  useEffect(() => {
    if (statuses?.length > 0 && !initialized) {
      const autoSelected = statuses
        .map((s) => s.status)
        .filter((name) => !DEFAULT_UNSELECTED_ORDER_STATUSES.includes(name));
      setSelectedStatuses(autoSelected);
      setInitialized(true);
    }
  }, [statuses, initialized]);

  const dashboardEndpoint = useMemo(() => {
    if (!initialized || selectedStatuses.length === 0) {
      return PAYMENT_ENDPOINTS.DASHBOARD;
    }
    const param = encodeURIComponent(selectedStatuses.join(','));
    return `${PAYMENT_ENDPOINTS.DASHBOARD}?order_statuses=${param}`;
  }, [selectedStatuses, initialized]);

  const { data, isLoading, error } = useFetchData(
    dashboardEndpoint, true, initialized
  );

  const toggleStatus = (statusName) => {
    setSelectedStatuses((prev) =>
      prev.includes(statusName)
        ? prev.filter((s) => s !== statusName)
        : [...prev, statusName]
    );
  };

  const isFiltered = statuses?.length > 0 &&
    selectedStatuses.length !== statuses.length;

  if (!initialized || isLoading) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={1.5}>
          {[1, 2, 3, 4].map((i) => (
            <Grid xs={6} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid xs={12} md={6}>
            <Skeleton variant="text" width={120} height={24} sx={{ mb: 1 }} />
            <Grid container spacing={1}>
              {[1, 2, 3, 4].map((i) => (
                <Grid xs={6} sm={3} key={i}>
                  <Skeleton variant="rounded" height={80} />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid xs={12} md={6}>
            <Skeleton variant="text" width={140} height={24} sx={{ mb: 1 }} />
            <Grid container spacing={1}>
              {[1, 2, 3].map((i) => (
                <Grid xs={4} key={i}>
                  <Skeleton variant="rounded" height={80} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid xs={12} lg={7}>
            <Skeleton variant="text" width={180} height={24} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={300} />
          </Grid>
          <Grid xs={12} lg={5}>
            <Skeleton variant="text" width={200} height={24} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load dashboard data. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
          Payment Dashboard
        </Typography>
        <Tooltip title="Filters">
          <IconButton
            onClick={() => setFiltersOpen((prev) => !prev)}
            color={filtersOpen || isFiltered ? 'primary' : 'default'}
            aria-label="toggle filters"
          >
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={filtersOpen}>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={500}
            sx={{ color: 'text.secondary', mb: 0.75, display: 'block' }}
          >
            Order Status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {statuses?.map((s) => {
              const isSelected = selectedStatuses.includes(s.status);
              return (
                <Chip
                  key={s.id}
                  label={s.status}
                  size="small"
                  variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  onClick={() => toggleStatus(s.status)}
                  sx={{ transition: 'all 0.15s ease' }}
                />
              );
            })}
          </Box>
        </Box>
      </Collapse>

      <PaymentCurrencyLanes data={data} selectedStatuses={selectedStatuses} />

      <Grid container spacing={2} sx={{ mt: 2, alignItems: 'stretch' }}>
        <Grid xs={12} md={6} sx={{ display: 'flex' }}>
          <OverdueAgingSection data={data?.overdue_aging} selectedStatuses={selectedStatuses} />
        </Grid>
        <Grid xs={12} md={6} sx={{ display: 'flex' }}>
          <ProvisionForecastSection data={data?.provision_forecast} selectedStatuses={selectedStatuses} />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{ color: 'text.primary' }}
        >
          Cash Flow - Last 6 Months
        </Typography>
        <CashFlowChart data={data?.cash_flow} />
      </Box>
    </Box>
  );
};

export default PaymentDashboard;
